import { useEffect, useState } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaCog,
  FaHome,
  FaTimes,
} from "react-icons/fa";
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { Scrollbars } from "rc-scrollbars";
import logo from "../../assets/images/logo-white.png";

import Collapse from "./../../assets/images/hamburger.png";
import { PiSignOut } from "react-icons/pi";
import { useSelector } from "react-redux";
import { getRemindersLinks } from "../../services/authService";
import { StarOutlined } from "@ant-design/icons";
import { HiSparkles } from "react-icons/hi2";
import WhatsAppLinkModal from "../WhatsAppLinkModal";

const SideBar = ({ mobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const [collapseMenu, setCollapseMenu] = useState(false);
  const isMobile = useMediaQuery({ query: "(max-width: 992px)" });
  const { email, is_slack_connected, is_team_connected, is_whatsapp_user } =
    useSelector((state) => state.auth);
  const [reminderLinks, setReminderLinks] = useState({
    teams: "#",
    slack: "#",
  });
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  useEffect(() => {
    getRemindersLinks(email).then((res) => {
      setReminderLinks({
        teams: `https://teams.microsoft.com/l/app/${process.env.REACT_APP_TEAMS_APP_ID}?source=app-details-dialog`,
        slack: res,
      });
    });
  }, [email]);

  // On mobile: close sidebar when navigating
  const handleNavClick = () => {
    if (isMobile) onMobileClose?.();
  };

  const showText = isMobile || !collapseMenu;
  const isInTeams = window.self !== window.top;

  return (
    <>
      {/* Dark backdrop — mobile only */}
      {isMobile && (
        <div
          onClick={onMobileClose}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            zIndex: 1049,
            pointerEvents: mobileOpen ? "auto" : "none",
            opacity: mobileOpen ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        />
      )}

      {/* Sidebar panel */}
      <div
        className="vh-100 bg-primary side-menu d-flex flex-column"
        style={
          isMobile
            ? {
                position: "fixed",
                top: 0,
                left: 0,
                zIndex: 1050,
                width: "260px",
                transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
                transition: "transform 0.3s ease-in-out",
              }
            : { position: "relative" }
        }
      >
        {/* Header row: logo + toggle */}
        <div className="d-flex w-100 align-items-center justify-content-between py-4 my-lg-3">
          {showText && (
            <Link to="/" className="ps-3 text-white fw-bold h2">
              <img src={logo} alt="logo" height={32} width={175} />
            </Link>
          )}
          {isMobile ? (
            <button
              onClick={onMobileClose}
              className="btn p-0 px-3 text-white ms-auto"
              aria-label="Close menu"
            >
              <FaTimes size={20} />
            </button>
          ) : (
            <Link
              className="px-3"
              to="#"
              onClick={(e) => {
                e.preventDefault();
                setCollapseMenu(!collapseMenu);
              }}
            >
              <img src={Collapse} alt="hamburger" />
            </Link>
          )}
        </div>

        {/* Scrollable menu area */}
        <div className="flex-grow-1" style={{ minHeight: 0 }}>
          <Scrollbars
            autoHide
            autoHeight
            autoHeightMax="calc(100vh - 215px)"
            renderThumbVertical={({ style, ...props }) => (
              <div
                {...props}
                style={{ ...style, backgroundColor: "#CCC", borderRadius: "4px" }}
              />
            )}
          >
            <Sidebar
              className="border-0"
              rootStyles={{
                "& .ps-sidebar-container, &.ps-submenu-content": {
                  backgroundColor: "transparent !important",
                  color: "#CCC",
                  fontSize: "1rem",
                },
                "& .ps-sidebar-root": {
                  border: "none !important",
                },
                "&.ps-collapsed": {
                  width: "50px",
                  minWidth: "50px",
                  "& .ps-submenu-content .ps-menu-button": {
                    paddingLeft: "30px !important",
                    paddingRight: "10px",
                  },
                },
              }}
              collapsed={!isMobile && collapseMenu}
            >
              <Menu
                menuItemStyles={{
                  button: {
                    [`&.ps-active, &:hover`]: {
                      color: "#28BF8D",
                      background: "transparent",
                      fontWeight: 700,
                    },
                  },
                }}
                renderExpandIcon={({ open }) =>
                  open ? (
                    <FaChevronDown className="text-sm" />
                  ) : (
                    <FaChevronRight className="text-sm" />
                  )
                }
              >
                <MenuItem
                  icon={<FaHome />}
                  active={location.pathname === "/dashboard"}
                  component={<Link to="/dashboard" onClick={handleNavClick} />}
                >
                  Pulse Center
                </MenuItem>
                <MenuItem
                  icon={<StarOutlined />}
                  active={location.pathname.startsWith("/check-in")}
                  component={<Link to="/check-in" onClick={handleNavClick} />}
                >
                  Check-In
                </MenuItem>
                <MenuItem
                  icon={<HiSparkles />}
                  active={location.pathname === "/my-insights"}
                  component={<Link to="/my-insights" onClick={handleNavClick} />}
                >
                  My Insights
                </MenuItem>
                <MenuItem
                  icon={<FaCog />}
                  active={location.pathname === "/settings"}
                  component={<Link to="/settings" onClick={handleNavClick} />}
                >
                  Settings
                </MenuItem>
                <MenuItem
                  icon={<PiSignOut />}
                  active={location.pathname === "/logout"}
                  component={<Link to="/logout" onClick={handleNavClick} />}
                >
                  Logout
                </MenuItem>
              </Menu>
            </Sidebar>
          </Scrollbars>
        </div>

        {/* Bottom integration links */}
        <div className="pb-5" style={{ fontSize: "1rem" }}>
          {!isInTeams && !is_slack_connected && (
            <Link
              to={reminderLinks.slack}
              className="d-flex align-items-center gap-2 ps-4 py-2 text-white"
              target="_blank"
            >
              <img
                src="https://a.slack-edge.com/80588/marketing/img/icons/icon_slack_hash_colored.png"
                alt="Slack Logo"
                height={20}
              />
              {showText && "Add To Slack"}
            </Link>
          )}
          {!isInTeams && !is_whatsapp_user && (
            <button
              onClick={() => setShowWhatsAppModal(true)}
              className="d-flex align-items-center gap-2 ps-4 py-2 text-white border-0 bg-transparent w-100"
              style={{ cursor: "pointer" }}
            >
              <img
                src="https://cdn2.iconfinder.com/data/icons/social-messaging-ui-color-shapes-2-free/128/social-whatsapp-circle-1024.png"
                alt="WhatsApp Logo"
                height={20}
              />
              {showText && "Add To WhatsApp"}
            </button>
          )}
          <WhatsAppLinkModal
            show={showWhatsAppModal}
            onHide={() => setShowWhatsAppModal(false)}
          />
          {!isInTeams && !is_team_connected && (
            <Link
              to={reminderLinks.teams}
              className="d-flex align-items-center gap-2 ps-4 py-2 text-white"
              target="_blank"
            >
              <img
                src="https://mailmeteor.com/logos/assets/PNG/Microsoft_Office_Teams_Logo_128px.png"
                alt="Teams Logo"
                height={20}
              />
              {showText && "Add To Teams"}
            </Link>
          )}
        </div>
      </div>
    </>
  );
};

export default SideBar;
